import { TvController } from './tvController';
import { subtreeTags, TvControllerGroup } from './tvControllerGroup';

/** Where a TV stands against the guide of the parent group it belongs to. */
export type GuideState =
  | 'ok' // loaded the current version of its group's guide
  | 'outdated' // loaded an older version, or another guide
  | 'unknown' // never loaded through the agent since hashes were introduced
  | 'pending' // the agent has not hashed the group's guide yet
  | 'none' // not in a parent group with a guide
  | 'conflict'; // in two parent groups with different guides

export type GuideStatus = {
  state: GuideState;
  /** The guide the TV should have (absent for none/conflict). */
  guideId?: string;
};

type GuideLike = { id?: string; hash?: string };

/** tag → guide ids of the parent groups whose subtree uses that tag. */
export function guidesByTag(groups: TvControllerGroup[]) {
  const map = new Map<string, Set<string>>();
  groups.forEach((parent) => {
    if (!parent.channelGuideId) return;
    subtreeTags(parent).forEach((tag) => {
      const set = map.get(tag) ?? new Set<string>();
      set.add(parent.channelGuideId!);
      map.set(tag, set);
    });
  });
  return map;
}

export function guideStatus(
  tv: TvController,
  byTag: Map<string, Set<string>>,
  guides: Map<string, GuideLike>
): GuideStatus {
  const ids = new Set<string>();
  (tv.tags || []).forEach((tag) =>
    byTag.get(tag)?.forEach((id) => ids.add(id))
  );
  if (ids.size === 0) return { state: 'none' };
  if (ids.size > 1) return { state: 'conflict' };
  const guideId = [...ids][0];
  const hash = guides.get(guideId)?.hash;
  if (!hash) return { state: 'pending', guideId };
  const loaded = tv.loadedGuide;
  if (!loaded) return { state: 'unknown', guideId };
  if (loaded.id !== guideId || loaded.hash !== hash)
    return { state: 'outdated', guideId };
  return { state: 'ok', guideId };
}

/** TVs of a parent group (and its subgroups). */
export function groupTvs(group: TvControllerGroup, tvs: TvController[]) {
  const tags = new Set(subtreeTags(group));
  return tvs.filter((tv) => (tv.tags || []).some((tag) => tags.has(tag)));
}

/** Parent (top-level) group holding the group with `id`. */
export function parentGroupOf(
  groups: TvControllerGroup[],
  id: string
): TvControllerGroup | undefined {
  const contains = (node: TvControllerGroup): boolean =>
    node.id === id || node.children.some(contains);
  return groups.find(contains);
}

export const GUIDE_STATE_LABEL: Record<GuideState, string> = {
  ok: 'Up to date',
  outdated: 'Out of date',
  unknown: 'Unknown',
  pending: 'Pending',
  none: 'No group guide',
  conflict: 'Conflict',
};
